import { tabsAnatomy as parts } from '@chakra-ui/anatomy';
import { cssVar } from '@chakra-ui/react';
import type {
  PartsStyleFunction,
  PartsStyleInterpolation,
  SystemStyleFunction,
} from '@chakra-ui/theme-tools';

import { transparentize } from '../utilities/transparentize';

const $fg = cssVar('tabs-color');
const $bg = cssVar('tabs-bg');
const $border = cssVar('tabs-border-color');

const baseStyleTab: SystemStyleFunction = ({ isFitted }) => ({
  flex: isFitted ? 1 : undefined,
  transitionProperty: 'common',
  transitionDuration: 'normal',
  _focusVisible: {
    zIndex: 1,
    boxShadow: 'outline',
  },
  _disabled: {
    cursor: 'not-allowed',
    opacity: 0.4,
  },
});

const baseStyle: PartsStyleFunction<typeof parts> = (props) => ({
  tab: baseStyleTab(props),
});

const variantLine: PartsStyleFunction<typeof parts> = (props) => {
  const { orientation } = props;
  const isVertical = orientation === 'vertical';
  const borderProp =
    orientation === 'vertical' ? 'borderStart' : 'borderBottom';
  const marginProp = isVertical ? 'marginStart' : 'marginBottom';

  return {
    tablist: {
      [borderProp]: '1px solid',
      borderColor: 'stroke.grey',
    },
    tab: {
      [borderProp]: '3px solid',
      [$border.variable]: 'transparent',
      [$fg.variable]: 'colors.text.tertiary',
      [marginProp]: '-1px',
      _selected: {
        [$fg.variable]: 'colors.accent.default',
        [$border.variable]: 'colors.accent.default',
        _hover: {
          [$bg.variable]: 'colors.accent.transparent',
        },
      },
      _hover: {
        [$fg.variable]: 'colors.text.secondary',
        [$bg.variable]: 'colors.blackAlpha.20',
      },
      color: $fg.reference,
      bg: $bg.reference,
      borderColor: $border.reference,
    },
  };
};

const variantLineSubTabs: PartsStyleFunction<typeof parts> = (props) => {
  const { orientation } = props;
  const isVertical = orientation === 'vertical';
  const borderProp =
    orientation === 'vertical' ? 'borderStart' : 'borderBottom';
  const marginProp = isVertical ? 'marginStart' : 'marginBottom';

  return {
    tablist: {
      [borderProp]: '1px solid',
      borderColor: 'stroke.grey',
    },
    tab: {
      [borderProp]: '2px solid',
      [$border.variable]: 'transparent',
      [$fg.variable]: 'colors.text.tertiary',
      fontSize: 'sm',
      fontWeight: 'semibold',
      pt: 4,
      px: 0,
      mx: 3,
      [marginProp]: '-1px',
      _selected: {
        [$fg.variable]: 'colors.text.secondary',
        [$border.variable]: 'colors.accent.default',
      },
      _hover: {
        [$fg.variable]: 'colors.text.secondary',
      },
      color: $fg.reference,
      borderColor: $border.reference,
    },
  };
};

const variantEnclosed: PartsStyleFunction<typeof parts> = () => {
  return {
    tab: {
      borderTopRadius: 'md',
      border: '1px solid',
      borderColor: 'transparent',
      [$fg.variable]: 'colors.text.tertiary',
      mb: '-1px',
      _selected: {
        [$fg.variable]: 'colors.accent.default',
        [$border.variable]: `colors.white`,
        borderColor: 'stroke.grey',
        borderBottomColor: $border.reference,
        _hover: {
          [$fg.variable]: 'colors.accent.default',
        },
      },
      _hover: {
        [$fg.variable]: 'colors.text.secondary',
      },
      color: $fg.reference,
    },
    tablist: {
      mb: '-1px',
      borderBottom: '1px solid',
      borderColor: 'stroke.grey',
    },
  };
};

const variantEnclosedOnGrey: PartsStyleFunction<typeof parts> = () => {
  return {
    tab: {
      borderTopRadius: 'lg',
      border: '1px solid',
      borderColor: 'transparent',
      [$fg.variable]: 'colors.text.tertiary',
      [$bg.variable]: 'transparent',
      fontSize: 'sm',
      fontWeight: 'semibold',
      mb: '-1px',
      _selected: {
        [$fg.variable]: 'colors.text.secondary',
        [$bg.variable]: 'colors.white',
        borderColor: 'stroke.grey',
        borderBottomColor: 'white',
        _hover: {
          [$fg.variable]: 'colors.text.secondary',
        },
      },
      _hover: {
        [$fg.variable]: 'colors.text.secondary',
      },
      color: $fg.reference,
      backgroundColor: $bg.reference,
      backgroundClip: 'padding-box',
    },
    tablist: {
      mb: '-1px',
      borderBottom: '1px solid',
      borderColor: 'stroke.grey',
    },
  };
};

const variantEnclosedColored: PartsStyleFunction<typeof parts> = () => {
  return {
    tab: {
      border: '1px solid',
      borderColor: 'stroke.grey',
      borderBottomColor: 'stroke.grey-light',
      [$fg.variable]: 'colors.text.tertiary',
      [$bg.variable]: 'blackAlpha.50',
      mb: '-1px',
      _selected: {
        [$bg.variable]: 'colors.white',
        [$fg.variable]: 'colors.accent.default',
        borderColor: 'stroke.grey',
        borderBottomColor: 'transparent',
        _hover: {
          [$fg.variable]: 'colors.accent.default',
        },
      },
      _hover: {
        [$fg.variable]: 'colors.text.secondary',
      },
      color: $fg.reference,
      bg: $bg.reference,
    },
    tablist: {
      mb: '-1px',
      borderBottom: '1px solid',
      borderColor: 'stroke.grey',
    },
  };
};

const variantSoftRounded: PartsStyleFunction<typeof parts> = () => {
  return {
    tab: {
      borderRadius: 'full',
      fontWeight: 'semibold',
      [$fg.variable]: 'colors.text.tertiary',
      padding: '0 1rem',
      _selected: {
        [$fg.variable]: 'colors.accent.default',
        [$bg.variable]: 'colors.accent.transparent',
        _hover: {
          [$fg.variable]: 'colors.accent.default',
          [$bg.variable]: 'colors.accent.transparent',
        },
      },
      _hover: {
        [$fg.variable]: 'colors.text.secondary',
        [$bg.variable]: 'colors.blackAlpha.40',
      },
      color: $fg.reference,
      bg: $bg.reference,
    },
    tablist: {
      pb: 2,
      borderBottom: '1px solid',
      borderColor: 'stroke.grey',
    },
  };
};

const variantSolidRounded: PartsStyleFunction<typeof parts> = () => {
  return {
    tab: {
      borderRadius: 'full',
      fontWeight: 'semibold',
      padding: '0 1rem',
      [$fg.variable]: 'colors.text.tertiary',
      _selected: {
        [$fg.variable]: 'colors.white',
        [$bg.variable]: `colors.accent.default`,
        _hover: {
          [$fg.variable]: 'colors.white',
          [$bg.variable]: 'colors.accent.hover',
        },
      },
      _hover: {
        [$fg.variable]: 'colors.text.secondary',
        [$bg.variable]: 'colors.blackAlpha.40',
      },
      color: $fg.reference,
      bg: $bg.reference,
    },
    tablist: {
      pb: 2,
      borderBottom: '1px solid',
      borderColor: 'stroke.grey',
    },
  };
};

const variantOutlinedRounded: PartsStyleFunction<typeof parts> = (props) => {
  const { theme } = props;
  return {
    tab: {
      border: '1px solid',
      borderRadius: 'full',
      fontWeight: 'semibold',
      padding: '0 1rem',
      maxH: '1.5rem',
      [$fg.variable]: 'colors.text.tertiary',
      [$border.variable]: 'transparent',
      _dark: {
        [$fg.variable]: 'inherit',
      },
      _selected: {
        [$fg.variable]: 'colors.text.primary',
        [$bg.variable]: 'colors.accent.transparent',
        [$border.variable]: 'colors.accent.hover',
        _hover: {
          [$bg.variable]: transparentize(`accent.default`, 0.15)(theme),
        },
      },
      _hover: {
        [$fg.variable]: 'colors.text.secondary',
        [$bg.variable]: 'colors.blackAlpha.40',
      },
      color: $fg.reference,
      bg: $bg.reference,
      borderColor: $border.reference,
    },
    tablist: {
      pb: 2,
      borderBottom: '1px solid',
      borderColor: 'stroke.grey',
    },
  };
};

const variants: Record<string, PartsStyleInterpolation<typeof parts>> = {
  'admin-line': variantLine,
  'admin-line-sub-tabs': variantLineSubTabs,
  'admin-enclosed': variantEnclosed,
  'admin-enclosed-on-grey': variantEnclosedOnGrey,
  'admin-enclosed-colored': variantEnclosedColored,
  'admin-soft-rounded': variantSoftRounded,
  'admin-solid-rounded': variantSolidRounded,
  'admin-outlined-rounded': variantOutlinedRounded,
};

export const Tabs = {
  baseStyle,
  variants,
};
